import React, { useMemo, useRef } from 'react';
import { hasAccess, userHasOnlyLicense } from 'cp-client-auth!sofe';
import { useClickEventStack } from 'canopy-styleguide!sofe';
import { useFeatureBubble, featureEnabled } from 'feature-toggles!sofe'
import { SecondaryNavbar } from './secondary-navbar.component';
import { CanopyLogo } from './components/canopy-logo.component';
import { GlobalAddButton } from './components/global-add-button.component';
import { NavItems } from './components/nav-items.component';
import { GlobalPlusNav } from './secondary-navs/global-plus-nav';
import { ClientsNav } from './secondary-navs/clients-nav';
import { FilesNav } from './secondary-navs/files-nav';
import { TasksNav } from './secondary-navs/tasks-nav';
import { TimeNav } from './secondary-navs/time-nav';
import { BillingNav } from './secondary-navs/billing-nav';
import { TemplatesNav } from './secondary-navs/templates-nav';
import { NavProfile } from './components/nav-profile/nav-profile.component';
import { useNav } from '../nav.context';
import { TimeIcon } from './components/time-icon.component';
import { InboxIcon } from './components/inbox-icon.component';

import styles from './primary-navbar.styles.css';
import { NavLearning } from './components/nav-learning.component';

const getTaskRegex = str => `^(?!.*global-settings).*#\/${str}*(?!.*client*)`; // not prepended by global setting and no form of 'client'
const taxPrepLicenses = ['tax_prep', 'taxPrep', 'taxPrepIndividual', 'taxPrepBusiness', 'tasks_organizers'];

export const PrimaryNavbar = props => {
  const globalPlus = useRef();
  const navItems = useRef();
  const navTools = useRef();
  const secondaryNav = useRef();

  const nav = useNav();
  const permissions = nav.user?.permissions
  const anyBilling = permissions && (permissions['billing_invoices'] || permissions['billing_payments'] || permissions['billing_credits'] || permissions['billing_service_items'] || permissions['billing_statements'] || permissions['billing_reports'] || permissions['billing_dashboard'])

  const newClientList = useFeatureBubble({
    toggle: 'toggle_client_list',
    title: 'New Client List',
    strategy: 'opt-in',
    expirationDate: '2023-05-01',
    hide: true,
  })

  const navItemList = useMemo(() => {
    if (!nav.user) return;
    const transcriptsStandalone = userHasOnlyLicense(nav.user, 'transcripts');

    return [
      {
        id: 'inbox',
        label: 'Inbox',
        component: InboxIcon,
        matches: ['#\\/inbox*'],
        enabled: permissions.communications,
      },
      {
        id: 'clients',
        label: newClientList ? 'Clients' : 'Contacts',
        icon: 'person-people',
        matches: [
          '^(?!.*global-settings).*#\/clients*(?!.*client*)',
          '^(?!.*global-settings).*#\/contacts'
        ],
        secondary: ClientsNav,
        enabled: permissions.contacts,
      },
      {
        id: 'files',
        label: transcriptsStandalone ? 'Transcripts' : 'Files',
        icon: 'folder-open',
        matches: ['^(?!.*global-settings).*#\\/files*', '#\\/transcripts*'],
        secondary: transcriptsStandalone ? null : FilesNav,
        url: transcriptsStandalone ? 'transcripts/list' : '',
        enabled: permissions.files,
      },
      {
        id: 'transcripts',
        label: 'Transcripts',
        icon: 'file-spreadsheet',
        url: 'transcripts/list',
        enabled: !permissions.files && permissions['transcripts'],
      },
      {
        id: 'tasks',
        label: transcriptsStandalone ? 'Notices' : 'Tasks',
        icon: 'checkmark-circle-open-large',
        matches: [
          getTaskRegex('task'),
          getTaskRegex('engagement'),
          getTaskRegex('notice'),
          getTaskRegex('tax-prep-tasks'),
        ],
        secondary: transcriptsStandalone ? null : TasksNav,
        url: transcriptsStandalone ? 'tasks/dashboard' : '',
        enabled:
          permissions.task_dashboard ||
          permissions.engagements ||
          taxPrepLicenses.some(l => hasAccess(nav.user)(l))
      },
      {
        id: 'time',
        label: 'Time',
        component: TimeIcon,
        matches: ['#\\/time*'],
        secondary: TimeNav,
        enabled: permissions.time_tracking,
      },
      {
        id: 'billing',
        label: 'Billing',
        icon: 'billing-square-open',
        matches: ['#\\/billing*'],
        secondary: BillingNav,
        enabled: anyBilling,
      },
      {
        id: 'templates',
        label: 'Templates',
        icon: 'organize-template',
        matches: [
          '#\\/letters/global-settings',
          '#\\/communications/global-settings',
          '#\\/files/global-settings',
          '#\\/notices/global-settings',
          '#\\/admin/global-settings',
          '#\\/tasks/global-settings',
          '#\\/notifications/global-settings',
          '#\\/templates',
        ],
        secondary: transcriptsStandalone ? null : TemplatesNav,
        url: transcriptsStandalone ? 'notices/global-settings' : '',
        enabled: true,
      },
      {
        id: 'insights',
        label: 'Insights',
        icon: 'misc-bar-graph',
        matches: ['#\\/insights*'],
        url: 'insights',
        enabled: featureEnabled('toggle_insights_pro'),
      },
    ];
  }, [nav.user, newClientList]);

  const navToolsList = useMemo(() => {
    if (!nav.user) return;
    return [
      {
        id: 'calendar',
        icon: 'misc-calendar',
        url: 'calendar',
        matches: ['\\/calendar(\\/(?!(clients)).*|$)'],
        enabled: hasAccess(nav.user)('communications'),
      },
      {
        id: 'learn',
        component: NavLearning,
        enabled: true,
      },
      {
        id: 'profile',
        component: NavProfile,
        enabled: true,
      },
    ];
  }, [nav.user]);


  const handleClickOutside = e => {
    if (
      globalPlus &&
      navItems &&
      navTools &&
      secondaryNav &&
      !globalPlus.current.contains(e.target) &&
      !navItems.current.contains(e.target) &&
      !navTools.current.contains(e.target) &&
      !secondaryNav.current.contains(e.target) &&
      !(e.target.closest && e.target.closest('.all-toasts')) &&
      !(e.target.closest && e.target.closest('.cp-dropdown'))
    ) {
      nav.setSecondaryOpen(null);
    }
  };

  useClickEventStack(handleClickOutside);

  const showGlobalAddMenu = show => {
    nav.setSecondaryOpen(show ? { id: 'globalplus', secondary: GlobalPlusNav } : null);
  };

  return (
    <div>
      <nav className={styles.navbar}>
        <div className={styles.navTop}>
          <div className={styles.logo}>
            <CanopyLogo />
          </div>
          <div ref={globalPlus} className={styles.globalAdd}>
            <GlobalAddButton showGlobalAddMenu={showGlobalAddMenu} />
          </div>
          <div ref={navItems} className={styles.navItems}>
            {navItemList && <NavItems items={navItemList} />}
          </div>
        </div>
        <div className={styles.navBottom}>
          <div ref={navTools} className={styles.navTools}>
            {navToolsList && <NavItems items={navToolsList} />}
          </div>
        </div>
      </nav>

      <div ref={secondaryNav}>
        <SecondaryNavbar show={nav.secondaryOpen} newClientList={newClientList} />
      </div>
    </div>
  );
};
